interface QuotaSummary {
  total_resources: number;
  resources_near_limit: number;
  resources_at_limit: number;
  by_provider: {
    [key: string]: {
      total: number;
      near_limit: number;
      at_limit: number;
    };
  };
  alerts: {
    active: number;
    unacknowledged: number;
  };
}

interface Props {
  summary: QuotaSummary;
}

export default function QuotaSummaryCards({ summary }: Props) {
  const healthScore = Math.max(
    0,
    100 - (summary.resources_near_limit / summary.total_resources) * 100
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {/* Total Resources */}
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm">Total Resources</p>
            <p className="text-3xl font-bold text-white">{summary.total_resources}</p>
          </div>
          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
        </div>
        <div className="mt-2">
          <span className="text-sm text-gray-400">Across all providers</span>
        </div>
      </div>

      {/* Resources Near Limit */}
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm">Near Limit (&gt;80%)</p>
            <p className="text-3xl font-bold text-yellow-400">{summary.resources_near_limit}</p>
          </div>
          <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>
        <div className="mt-2">
          <span className="text-sm text-gray-400">
            {summary.resources_near_limit > 0 ? 'Action recommended' : 'All resources healthy'}
          </span>
        </div>
      </div>

      {/* Active Alerts */}
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm">Active Alerts</p>
            <p className="text-3xl font-bold text-white">{summary.alerts.unacknowledged}</p>
          </div>
          <div className={`w-12 h-12 ${
            summary.alerts.unacknowledged > 0 ? 'bg-red-500/20' : 'bg-green-500/20'
          } rounded-lg flex items-center justify-center`}>
            <svg className={`w-6 h-6 ${
              summary.alerts.unacknowledged > 0 ? 'text-red-500' : 'text-green-500'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
        </div>
        <div className="mt-2">
          <span className={`text-sm ${
            summary.alerts.unacknowledged > 0 ? 'text-red-400' : 'text-green-400'
          }`}>
            {summary.alerts.unacknowledged > 0 ? 'Requires attention' : 'No pending alerts'}
          </span>
        </div>
      </div>

      {/* Health Score */}
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm">Health Score</p>
            <p className="text-3xl font-bold text-white">{healthScore.toFixed(0)}%</p>
          </div>
          <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="mt-2">
          <div className="w-full bg-gray-800 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                healthScore >= 80 ? 'bg-green-500' :
                healthScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${healthScore}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}